import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import API from "../api/api01";

interface Course {
  id: string;
  code: string;
  name: string;
  credits: number;
  faculty: string;
  seats: string;
  is_registered: boolean;
}

export function CourseRegistration() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = userFromStorage?.id;

  useEffect(() => {
    if (studentId) {
      fetchCourses();
    } else {
      setLoading(false);
    }
  }, [studentId]);

  const fetchCourses = async () => {
    try {
      const response = await API.get(`/superadmin/student/registration-data/${studentId}`);
      setCourses(response.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to load available courses");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterToggle = async (courseId: string, courseCode: string) => {
    try {
      const response = await API.post("/superadmin/student/register-course-toggle", {
        student_id: studentId,
        course_id: courseId
      });

      const newStatus = response.data.status;

      setCourses(prev => prev.map(c =>
        c.id === courseId ? { ...c, is_registered: newStatus === "registered" } : c
      ));

      toast.success(
        newStatus === "registered"
          ? `Registered for ${courseCode}`
          : `Deregistered from ${courseCode}`
      );
    } catch (error) {
      console.error("Error toggling registration:", error);
      toast.error("Action failed. Please try again.");
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <BookOpen className="h-6 w-6" />
          Course Registration
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Register or deregister for available courses for the upcoming semester
        </p>
      </div>

      {/* Course List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Courses – Upcoming Semester</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : courses.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground">No courses available for registration yet.</p>
          ) : (
            courses.map((course) => {
              const isRegistered = course.is_registered;
              return (
                <div
                  key={course.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {course.code}
                      </Badge>
                      <p className="font-medium">{course.name}</p>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5">
                      {course.faculty} • {course.credits} Credits • Seats: {course.seats}
                    </p>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => handleRegisterToggle(course.id, course.code)}
                    className={`text-white ${isRegistered
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-blue-600 hover:bg-blue-700"
                      }`}
                  >
                    {isRegistered ? "Registered" : "Register"}
                  </Button>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
